import { useCallback, useEffect, useRef, useState } from "react"
import { useLocation, useNavigate, useSearchParams } from "react-router-dom"
import { motion } from "framer-motion"
import { CheckCircle2, Loader2 } from "lucide-react"

import { Skeleton } from "@/components/ui/skeleton"
import { NetworkMembroItem, NetworkMeta } from "@/types/membros"
import {
  fetchMembrosAction,
  getAllMemberStates,
  getMemberCategories,
  getMemberSubcategories,
} from "@/modules/Users/service/UsersService"
import { getFavoriteGroups } from "../services/favoritesService"
import { MemberCard } from "./MemberCard"
import { NetworkFilters } from "./NetworkingFilters"

const PAGE_LIMIT = 30

export function NetworkTab() {
  const location = useLocation()
  const [searchParams, setSearchParams] = useSearchParams()
  const navigate = useNavigate()
  const { evento } = location.state || {}

  const [membros, setMembros] = useState<NetworkMembroItem[]>([])
  const [meta, setMeta] = useState<NetworkMeta | null>(null)
  const [favoriteIds, setFavoriteIds] = useState<Set<string>>(new Set())
  const [loading, setLoading] = useState(true)
  const [loadingMore, setLoadingMore] = useState(false)
  const [page, setPage] = useState(1)
  const [states, setStates] = useState<{ nome: string; sigla: string }[]>([])
  const [categories, setCategories] = useState<
    { catappId: number; catappDescricao: string }[]
  >([])
  const [subcategories, setSubcategories] = useState<
    { subcatappId: number; subcatappDescricao: string; subcatappCatappId: number }[]
  >([])
  const loaderRef = useRef<HTMLDivElement>(null)

  const search = searchParams.get("search") || ""
  const stateFilter = searchParams.get("stateFilter") || "todos"
  const categoriaFilter = searchParams.get("categoriaFilter") || "todos"
  const subcategoriaFilter = searchParams.get("subcategoriaFilter") || "todos"

  const [inputValue, setInputValue] = useState(search)

  // Debounce: atualiza searchParams só após o usuário parar de digitar
  useEffect(() => {
    const timer = setTimeout(() => {
      setSearchParams(
        (prev) => {
          if (inputValue) prev.set("search", inputValue)
          else prev.delete("search")
          return prev
        },
        { replace: true }
      )
    }, 400)
    return () => clearTimeout(timer)
  }, [inputValue])

  const setStateFilter = (value: string) => {
    setSearchParams(
      (prev) => {
        if (value !== "todos") prev.set("stateFilter", value)
        else prev.delete("stateFilter")
        return prev
      },
      { replace: true }
    )
  }

  const setCategoriaFilter = (value: string) => {
    setSearchParams(
      (prev) => {
        if (value !== "todos") prev.set("categoriaFilter", value)
        else prev.delete("categoriaFilter")
        return prev
      },
      { replace: true }
    )
  }

  const setSubcategoriaFilter = (value: string) => {
    setSearchParams(
      (prev) => {
        if (value !== "todos") prev.set("subcategoriaFilter", value)
        else prev.delete("subcategoriaFilter")
        return prev
      },
      { replace: true }
    )
  }

  useEffect(() => {
    getFavoriteGroups()
      .then((groups) => {
        const favIds = new Set<string>()
        groups.forEach((g) => g.pessoas.forEach((p) => favIds.add(p.uid)))
        setFavoriteIds(favIds)
      })
      .catch(() => {})
  }, [])

  useEffect(() => {
    getAllMemberStates()
      .then(({ data }) => {
        if (Array.isArray(data.data)) setStates(data.data)
      })
      .catch(() => {})
  }, [])

  useEffect(() => {
    getMemberCategories()
      .then(({ data }) => {
        if (Array.isArray(data.data)) setCategories(data.data)
      })
      .catch(() => {})
  }, [])

  useEffect(() => {
    if (categoriaFilter === "todos") {
      setSubcategories([])
      return
    }
    getMemberSubcategories(Number(categoriaFilter))
      .then(({ data }) => {
        if (Array.isArray(data.data)) setSubcategories(data.data)
      })
      .catch(() => {})
  }, [categoriaFilter])

  useEffect(() => {
    const loadInitial = async () => {
      setLoading(true)
      setPage(1)
      setMembros([])
      try {
        const params: Record<string, any> = { page: 1, limit: PAGE_LIMIT }
        if (search) params.search = search
        if (stateFilter !== "todos") params.estado = stateFilter
        if (categoriaFilter !== "todos") params.categoria = Number(categoriaFilter)
        if (subcategoriaFilter !== "todos") params.subcategoria = Number(subcategoriaFilter)
        if (evento) {
          params.origem = evento.origem
          params.id_origem = evento.id
        }
        const { data } = await fetchMembrosAction(params)
        setMembros(data.data ?? [])
        setMeta(data.meta ?? null)
      } catch (error) {
        console.error(error)
      } finally {
        setLoading(false)
      }
    }
    loadInitial()
  }, [search, stateFilter, categoriaFilter, subcategoriaFilter, location.state])

  useEffect(() => {
    if (page === 1) return
    const loadMore = async () => {
      setLoadingMore(true)
      try {
        const params: Record<string, any> = { page, limit: PAGE_LIMIT }
        if (search) params.search = search
        if (stateFilter !== "todos") params.estado = stateFilter
        if (categoriaFilter !== "todos") params.categoria = Number(categoriaFilter)
        if (subcategoriaFilter !== "todos") params.subcategoria = Number(subcategoriaFilter)
        if (evento) {
          params.origem = evento.origem
          params.id_origem = evento.id
        }
        const { data } = await fetchMembrosAction(params)
        setMembros((prev) => [...prev, ...(data.data ?? [])])
        setMeta(data.meta ?? null)
      } catch (error) {
        console.error(error)
      } finally {
        setLoadingMore(false)
      }
    }
    loadMore()
  }, [page])

  const handleObserver = useCallback(
    (entries: IntersectionObserverEntry[]) => {
      const target = entries[0]
      if (target.isIntersecting && meta?.hasMore && !loadingMore && !loading) {
        setPage((prev) => prev + 1)
      }
    },
    [meta?.hasMore, loadingMore, loading]
  )

  useEffect(() => {
    const observer = new IntersectionObserver(handleObserver, { rootMargin: "400px" })
    if (loaderRef.current) observer.observe(loaderRef.current)
    return () => observer.disconnect()
  }, [handleObserver])

  return (
    <div className="space-y-8">
      {!evento && (
        <NetworkFilters
          search={inputValue}
          onSearchChange={setInputValue}
          stateFilter={stateFilter}
          onStateFilterChange={setStateFilter}
          states={states}
          categoriaFilter={categoriaFilter}
          onCategoriaFilterChange={setCategoriaFilter}
          subcategoriaFilter={subcategoriaFilter}
          onSubcategoriaFilterChange={setSubcategoriaFilter}
          categories={categories}
          subcategories={subcategories}
        />
      )}

      <div className="min-h-[400px] pb-20">
        <p className="my-2 text-zinc-400">
          {meta
            ? `${meta.total} ${meta.total === 1 ? "Membro encontrado" : "Membros encontrados"}`
            : " "}
        </p>

        {loading ? (
          <div className="grid grid-cols-1 gap-4 md:grid-cols-2 xl:grid-cols-3">
            {[...Array(6)].map((_, i) => (
              <Skeleton
                key={i}
                className="h-28 w-full rounded-2xl bg-zinc-900"
              />
            ))}
          </div>
        ) : (
          <motion.div
            key={search}
            initial="hidden"
            animate="visible"
            variants={{ visible: { transition: { staggerChildren: 0.04 } } }}
            className="grid grid-cols-1 gap-4 lg:grid-cols-2 xl:grid-cols-3"
          >
            {membros.map((membro) => (
              <MemberCard
                key={membro.pesUid}
                membro={membro}
                isFavorite={favoriteIds.has(membro.pesUid)}
                onClick={(id) => {
                  navigate(`/network/member/${id}?${searchParams.toString()}`)
                }}
              />
            ))}
          </motion.div>
        )}

        <div
          ref={loaderRef}
          className="mt-12 flex h-20 w-full items-center justify-center"
        >
          {loadingMore && (
            <div className="flex items-center gap-2 text-xs font-bold uppercase tracking-widest text-zinc-600">
              <Loader2 className="size-4 animate-spin text-primary" />
              Carregando...
            </div>
          )}
          {!loading && !loadingMore && meta && !meta.hasMore && membros.length > 0 && (
            <div className="flex items-center gap-2 text-xs font-bold uppercase tracking-widest text-zinc-600">
              <CheckCircle2 className="size-4 text-green-500/70" />
              Todos os membros foram carregados
            </div>
          )}
        </div>
      </div>
    </div>
  )
}
